// Workflow Copilot — CLIENT tool implementations.
//
// The server loop pauses on every tool call and hands it here; each tool runs
// against the LIVE canvas via ctx:
//   ctx.getWorkflowState() → current workflowState
//   ctx.applyEdits(ops)    → { applied, errors }  (WorkflowCanvas → applyCopilotEdits → setWorkflowState)
//
// Read tools answer from the compressed representation (same short keys the
// model was taught); write tools reuse the workflowCopilotEdits op vocabulary,
// so a tool result of { ok, did } means the canvas ALREADY changed.

var codec = require('./workflowCodec');
var apiClient = require('./apiClient');
var copilotEdits = require('./workflowCopilotEdits');

var REVIEW_ENDPOINT = '/workflow-copilot/review';

// Node fields that are heavy and useless to the model. lastOutput especially
// can carry hundreds of KB of captured run output (see the save-payload caps).
var HEAVY_NODE_FIELDS = ['lastOutput', 'stats', 'changeHistory'];
var CODE_RESULT_CAP = 6000;
var GREP_MAX_NODES = 20;
var GREP_MAX_EXCERPTS = 3;

function compressForCopilot(workflowState) {
  var state = workflowState || {};
  var slim = Object.assign({}, state, {
    nodes: (state.nodes || []).map(function(node) {
      var copy = Object.assign({}, node);
      HEAVY_NODE_FIELDS.forEach(function(field) { delete copy[field]; });
      return copy;
    })
  });
  return codec.compressWorkflow(slim);
}

function capNodeCode(node) {
  if (!node || typeof node.co !== 'string' || node.co.length <= CODE_RESULT_CAP) return node;
  var capped = Object.assign({}, node);
  capped.co = node.co.slice(0, CODE_RESULT_CAP) +
    '\n/* ...TRUNCATED (' + node.co.length + ' chars total) — do NOT edit this node\'s code */';
  return capped;
}

function nodeTypeTag(node) {
  var ti = String(node.ti || '');
  if (ti.indexOf('sys-') === 0) return ti.slice(4);
  return 'custom';
}

// --- Read tools -------------------------------------------------------------

// Grep-style investigation over the compressed workflow. Like a code grep:
// alternation patterns ("reddit|subreddit|rss"), optional scoping to one node
// (nodeId) or specific fields (fields: ["co","gp"]), and hits reported as
// field + line + trimmed excerpt so the model sees WHERE each match lives.
function toolGrepWorkflow(args, wf) {
  var a = args || {};
  var pattern = String(a.pattern || '').trim();
  if (!pattern) return { error: 'pattern is required' };
  var re;
  try { re = new RegExp(pattern, 'i'); }
  catch (e) { return { error: 'Invalid regex: ' + e.message }; }

  var scopeNodeId = String(a.nodeId || '').trim() || null;
  var scopeFields = Array.isArray(a.fields) && a.fields.length > 0
    ? a.fields.map(String)
    : null;

  function hitLine(text) {
    var trimmed = String(text).trim();
    return trimmed.length > 160 ? trimmed.slice(0, 160) + '…' : trimmed;
  }

  // Walk one node field: strings are searched line by line (so code/prompts
  // report line numbers); everything else is matched on its JSON form.
  function fieldHits(key, value) {
    var found = [];
    if (typeof value === 'string') {
      var lines = value.split('\n');
      for (var li = 0; li < lines.length; li++) {
        if (re.test(lines[li])) {
          found.push(lines.length > 1
            ? { f: key, ln: li + 1, x: hitLine(lines[li]) }
            : { f: key, x: hitLine(lines[li]) });
          if (found.length >= GREP_MAX_EXCERPTS) break;
        }
      }
    } else if (value !== null && value !== undefined) {
      var json = JSON.stringify(value);
      if (re.test(json)) found.push({ f: key, x: hitLine(json) });
    }
    return found;
  }

  var nodeMatches = [];
  var totalHits = 0;
  var truncated = false;
  (wf.n || []).forEach(function(node) {
    if (scopeNodeId && node.i !== scopeNodeId) return;
    var hits = [];
    Object.keys(node).forEach(function(key) {
      if (scopeFields && scopeFields.indexOf(key) === -1) return;
      hits = hits.concat(fieldHits(key, node[key]));
    });
    if (hits.length === 0) return;
    totalHits += hits.length;
    if (nodeMatches.length >= GREP_MAX_NODES) { truncated = true; return; }
    nodeMatches.push({ i: node.i, t: node.t || node.l || '(untitled)', hits: hits.slice(0, GREP_MAX_EXCERPTS * 2) });
  });

  var connMatches = scopeNodeId || scopeFields ? [] : (wf.c || []).filter(function(conn) {
    return re.test(JSON.stringify(conn));
  });

  var result = { pattern: pattern, totalHits: totalHits, nodes: nodeMatches, connections: connMatches };
  if (truncated) result.note = 'More than ' + GREP_MAX_NODES + ' nodes matched — showing the first ' + GREP_MAX_NODES + '. Narrow the pattern or scope with nodeId/fields.';
  if (nodeMatches.length === 0 && connMatches.length === 0) result.note = 'No matches.';
  return result;
}

function toolGetNode(args, wf, state, liveVars) {
  var nodeId = String((args && args.nodeId) || '').trim();
  if (!nodeId) return { error: 'nodeId is required' };
  var node = (wf.n || []).find(function(n) { return n.i === nodeId; });
  if (!node) return { error: 'No node with id "' + nodeId + '". Use listNodes or grepWorkflow to find the right id.' };
  var connections = (wf.c || []).filter(function(c) { return c.f === nodeId || c.t === nodeId; });
  return {
    node: capNodeCode(node),
    connections: connections,
    inputStream: describeInputStream(nodeId, wf, state || {}),
    variablesAvailable: listAvailableVariables(state || {}, liveVars)
  };
}

// What actually ARRIVES at this node at runtime, per incoming connection.
// Branch nodes do NOT pass their own input through — a consumer wired after a
// Classifier receives the classifier's result object, never the payload the
// classifier itself was fed. Stating this in every getNode stops edit prompts
// from guessing the input shape.
function describeInputStream(nodeId, wf, state) {
  var incoming = (wf.c || []).filter(function(c) { return c.t === nodeId; });
  if (incoming.length === 0) {
    return [{ note: 'No upstream connections — inputs is empty at runtime.' }];
  }
  var byId = {};
  (state.nodes || []).forEach(function(n) { if (n && n.id) byId[n.id] = n; });
  var entries = incoming.map(function(c) {
    var src = byId[c.f] || {};
    var icon = String(src.icon || '');
    var entry = { from: c.f, fromTitle: src.title || src.label || c.f };
    if (c.nd) {
      entry.passes = 'nothing — nd:true is an ordering-only connection, no data.';
      return entry;
    }
    if (icon === 'classifier' || icon === 'router') {
      entry.passes = 'the ' + icon + ' RESULT object { classification, reason, confidences } — NOT the payload the ' + icon + ' itself received. If this node needs those original fields, carry them via a VARIABLE: Set Variable upstream of the ' + icon + ' stores them, this node reads vars["..."]. NEVER add a direct edge from the original source — it bypasses the ' + icon + ' branch gate, so this node would run for EVERY payload instead of only its branch.';
    } else if (icon === 'condition') {
      entry.passes = 'the value the condition TESTED (its resolved input) — not a boolean.';
    } else if (icon === 'set-variable') {
      var varName = src.paramValues && src.paramValues.variable ? String(src.paramValues.variable) : '';
      entry.passes = 'the value it just stored' + (varName ? ' into "' + varName + '"' : '') + '.';
    } else {
      entry.passes = "that node's full output.";
    }
    if (c.pp) entry.passes += ' Only the "' + c.pp + '" field is passed (propPath).';
    entry.lastRunShape = src.lastOutput && src.lastOutput.shape !== undefined
      ? summarizeOutputFields(src.lastOutput.shape)
      : 'unknown — source has not run yet';
    return entry;
  });
  // The engine compacts inputs[]: only connections that FIRED in the wave get
  // a slot, so on multi-input nodes indexes shift (exclusive-branch merges
  // deliver the fired branch at inputs[0]).
  var dataConns = incoming.filter(function(c) { return !c.nd; }).length;
  if (dataConns > 1) {
    entries.push({ note: 'At runtime inputs[] holds ONLY the connections that fired in that wave, in the order above but COMPACTED (no gaps; nd edges never take a slot). On an exclusive-branch merge inputs[0] is whichever branch fired — never assume a fixed index per connection; detect the entry by shape or read from vars.' });
  }
  return entries;
}

// --- Live variable values (minimized) ---------------------------------------
// The copilot must SEE what each variable currently holds (a var whose live
// value is empty means the upstream writer is broken) without token blowups.
// Minimization reuses the shared apiClient helpers: minimizeArrays (deep,
// budgeted array trimming that collapses base64/data-URLs via
// minimizeStringBase64 at every depth) + a final JSON cap here, because a huge
// PLAIN-TEXT value (a full email body) passes those helpers untouched.

var VAR_VALUE_JSON_CAP = 1500;

function minimizeVariableValue(value) {
  var slim = apiClient.minimizeArrays(value);
  var json;
  try { json = JSON.stringify(slim); } catch (e) { json = String(slim); }
  if (typeof json === 'string' && json.length > VAR_VALUE_JSON_CAP) {
    return json.slice(0, VAR_VALUE_JSON_CAP) + '…(' + json.length + ' chars total)';
  }
  return slim;
}

// Ephemeral live values (last run / Set Variable previews) from the canvas,
// keyed by variable name. Optional — tools degrade to saved defaults.
function getLiveVariableValues(ctx) {
  return ctx && typeof ctx.getVariableValues === 'function' ? (ctx.getVariableValues() || {}) : {};
}

// Declared workflow variables (Variables nodes) — name + type + CURRENT value
// (live map wins, else the saved/default pv.value), readable in custom code
// via vars["Name"] and in params via @Name.
function listAvailableVariables(state, liveVars) {
  var live = liveVars && typeof liveVars === 'object' ? liveVars : {};
  return (state.nodes || [])
    .filter(function(n) { return n && n.icon === 'variables' && n.title; })
    .map(function(n) {
      var pv = n.paramValues || {};
      var name = String(n.title).trim();
      var entry = { name: n.title, dataType: String(pv.dataType || 'string') };
      if (Object.prototype.hasOwnProperty.call(live, name)) {
        entry.currentValue = minimizeVariableValue(live[name]);
      } else if (pv.value !== undefined && pv.value !== null && pv.value !== '') {
        entry.currentValue = minimizeVariableValue(pv.value);
      } else {
        entry.currentValue = '(unset)';
      }
      return entry;
    });
}

// Compact type/sample map of an output value so the model can decide whether
// to pass the full object or select one field (connection pp / propPath).
function summarizeOutputFields(value) {
  if (Array.isArray(value)) {
    var first = value[0];
    return {
      type: 'array',
      length: value.length,
      itemFields: first && typeof first === 'object' && !Array.isArray(first)
        ? Object.keys(first).slice(0, 30)
        : undefined
    };
  }
  if (value && typeof value === 'object') {
    var fields = {};
    Object.keys(value).slice(0, 30).forEach(function(key) {
      var v = value[key];
      if (Array.isArray(v)) fields[key] = { type: 'array', sample: 'length ' + v.length };
      else if (v && typeof v === 'object') fields[key] = { type: 'object', sample: 'keys: ' + Object.keys(v).slice(0, 8).join(', ') };
      else if (typeof v === 'string') fields[key] = { type: 'string', sample: v.length > 80 ? v.slice(0, 80) + '…' : v };
      else fields[key] = { type: v === null ? 'null' : typeof v, sample: v };
    });
    return { type: 'object', fields: fields };
  }
  return { type: value === null ? 'null' : typeof value, sample: value };
}

// Reads the node's persisted last-run output sample (node.lastOutput.shape,
// capped at capture time). Raw workflowState, NOT the compressed form — the
// compressed context strips lastOutput on purpose.
function toolGetNodeOutput(args, state) {
  var nodeId = String((args && args.nodeId) || '').trim();
  if (!nodeId) return { error: 'nodeId is required' };
  var node = (state.nodes || []).find(function(n) { return n && n.id === nodeId; });
  if (!node) return { error: 'No node with id "' + nodeId + '".' };
  if (!node.lastOutput || node.lastOutput.shape === undefined) {
    return { nodeId: nodeId, note: 'No run output recorded for this node yet — ask the user to run the workflow first if you need the real data shape.' };
  }
  var shape = node.lastOutput.shape;
  var sample = '';
  try { sample = JSON.stringify(shape); } catch (e) { sample = String(shape); }
  var truncated = sample.length > 4000;
  return {
    nodeId: nodeId,
    title: node.title || node.label || '',
    lastRunAt: node.lastOutput.at || undefined,
    summary: summarizeOutputFields(shape),
    sample: truncated ? sample.slice(0, 4000) + '…(truncated)' : sample
  };
}

function toolListNodes(_args, wf) {
  return {
    workflowName: wf.wn || '(unnamed)',
    nodes: (wf.n || []).map(function(node) {
      return { i: node.i, type: nodeTypeTag(node), t: node.t || node.l || '(untitled)' };
    }),
    connections: (wf.c || []).map(function(conn) {
      var out = { f: conn.f, t: conn.t };
      if (conn.i) out.i = conn.i;
      if (conn.br) out.br = conn.br;
      return out;
    })
  };
}

// --- Write tools ------------------------------------------------------------

// Custom/agent nodes are NOT created as empty shells — they go through the
// AI Flow Builder pipeline (ctx.generateCustomNode → useAiFlowGeneration.
// generateNodeAfterSource): the copilot's prompt drives real code generation,
// and the node lands on the canvas already wired after its source node.
function runGenerateCustomNode(args, ctx) {
  var a = args || {};
  var node = a.node || {};
  var prompt = String(a.prompt || node.gp || '').trim();
  var after = String(a.after || a.sourceNodeId || '').trim();
  if (!prompt) {
    return Promise.resolve({ error: 'addNode(custom/agent): provide "prompt" — a precise spec of what the node must do (inputs, action, outputs). The node\'s code is AI-generated from it.' });
  }
  if (!after) {
    return Promise.resolve({ error: 'addNode(custom/agent): provide "after" — the id of the source node the new node runs after (its output becomes the new node\'s input).' });
  }
  if (typeof ctx.generateCustomNode !== 'function') {
    return Promise.resolve({ error: 'AI node generation is not available right now.' });
  }
  // Same branch/label contracts as system addNode: a Condition source needs
  // branch:"yes"/"no", a Classifier/Router source needs label:"<pv.labels entry>"
  // so the new edge leaves the right output dot.
  var branch = String(a.branch || a.br || '').trim().toLowerCase();
  var sourceNode = ((ctx.getWorkflowState() || {}).nodes || []).find(function(n) { return n && n.id === after; });
  if (sourceNode && (sourceNode.templateId === 'sys-condition' || sourceNode.icon === 'condition') && branch !== 'yes' && branch !== 'no') {
    return Promise.resolve({ error: '"' + (sourceNode.title || after) + '" is a Condition node — pass branch:"yes" or branch:"no" to say which branch the new node attaches to.' });
  }
  var labelAnchor = null;
  if (sourceNode && copilotEdits.isLabelBranchNode(sourceNode)) {
    try {
      labelAnchor = copilotEdits.resolveLabelAnchor(sourceNode, a.label || a.branch).anchor;
    } catch (err) {
      return Promise.resolve({ error: (err && err.message) || String(err) });
    }
  }
  return Promise.resolve(ctx.generateCustomNode({
    sourceNodeId: after,
    prompt: prompt,
    agentMode: a.ty === 'agent' || !!a.agent,
    branch: branch === 'yes' || branch === 'no' ? branch : null,
    fromAnchor: labelAnchor,
    responseFields: Array.isArray(a.responseFields) ? a.responseFields : null
  })).then(function(res) {
    var made = res || {};
    return {
      ok: true,
      nodeId: made.nodeId,
      did: 'Generated node "' + (made.title || 'Node') + '" (' + made.nodeId + ') with AI code, connected after ' + after
    };
  }).catch(function(err) {
    return { error: 'Code generation failed: ' + ((err && err.message) || String(err)) };
  });
}

// Code/behavior edits on custom/agent nodes go through AskCode: the tool
// opens the AskCode popup on the node with the copilot's instructions
// injected (same path the Doctor uses via onFixNode), then WAITS for the
// apply to land — the node's code/prompt/changeHistory changing is the
// completion signal (AskCode auto-applies on this path). The loop must not
// continue (or claim success) while the editor is still generating.
var EDIT_POLL_MS = 2000;
var EDIT_TIMEOUT_MS = 180000;

function snapshotNodeEditState(state, id) {
  var node = ((state || {}).nodes || []).find(function(n) { return n && n.id === id; });
  if (!node) return null;
  return {
    code: node.code,
    gp: node.generationPrompt,
    hist: Array.isArray(node.changeHistory) ? node.changeHistory.length : 0,
    title: node.title || node.label || id
  };
}

function runEditNodeCode(args, ctx) {
  var a = args || {};
  var id = String(a.id || a.nodeId || '').trim();
  var prompt = String(a.prompt || '').trim();
  if (!id) return Promise.resolve({ error: 'editNodeCode: provide "id" — the node to edit.' });
  if (!prompt) return Promise.resolve({ error: 'editNodeCode: provide "prompt" — precise instructions for the code change.' });
  var node = ((ctx.getWorkflowState() || {}).nodes || []).find(function(n) { return n && n.id === id; });
  if (!node) return Promise.resolve({ error: 'No node with id "' + id + '".' });
  if (node.category !== 'custom') {
    return Promise.resolve({ error: '"' + (node.title || id) + '" is a system node with no code — use updateNode for its settings.' });
  }
  if (typeof ctx.editNodeWithAskCode !== 'function') {
    return Promise.resolve({ error: 'AskCode editing is not available right now.' });
  }

  var before = snapshotNodeEditState(ctx.getWorkflowState(), id);
  var timeoutMs = typeof a.timeoutMs === 'number' ? a.timeoutMs : EDIT_TIMEOUT_MS; // test hook
  var pollMs = Math.min(EDIT_POLL_MS, Math.max(10, Math.floor(timeoutMs / 4)));
  // Append the TRUE runtime input contract so AskCode never codes against a
  // guessed shape (e.g. assuming a classifier passes its input through), plus
  // the CURRENT variable values so it never reads a var that holds nothing.
  var stream = describeInputStream(id, compressForCopilot(ctx.getWorkflowState()), ctx.getWorkflowState() || {});
  var varsNow = listAvailableVariables(ctx.getWorkflowState() || {}, getLiveVariableValues(ctx));
  var fullPrompt = prompt
    + '\n\n[Authoritative runtime input contract — inputs[0] per incoming connection; trust this over any shape described above]\n' + JSON.stringify(stream, null, 1)
    + '\n\n[Current workflow variable values (minimized; long strings/base64 collapsed)]\n' + JSON.stringify(varsNow, null, 1);
  var ack = ctx.editNodeWithAskCode(id, fullPrompt);
  if (ack && ack.opened === false) {
    return Promise.resolve({ error: 'AskCode could not open on "' + before.title + '"' + (ack.reason ? ' — ' + ack.reason : '') + '. The edit did NOT run.' });
  }

  return new Promise(function(resolve) {
    var startedAt = Date.now();
    var timer = setInterval(function() {
      var now = snapshotNodeEditState(ctx.getWorkflowState(), id);
      if (!now) {
        clearInterval(timer);
        resolve({ error: '"' + before.title + '" disappeared while AskCode was editing it.' });
        return;
      }
      if (now.code !== before.code || now.gp !== before.gp || now.hist !== before.hist) {
        clearInterval(timer);
        resolve({ ok: true, did: 'AskCode applied the edit to "' + now.title + '"' });
        return;
      }
      // The editor closing (or moving to another node) with NO state change
      // means the ask died — user closed it, or AskCode finished without a
      // change. Report failure instead of pretending it's still working.
      var editor = typeof ctx.getAskCodeEditorState === 'function' ? ctx.getAskCodeEditorState() : null;
      if (editor && (editor.open === false || (editor.nodeId && editor.nodeId !== id))) {
        clearInterval(timer);
        resolve({ error: 'AskCode closed without changing "' + now.title + '" — it found NO change to make. That usually means the code ALREADY does what your prompt asked and your diagnosis is wrong. Re-read the node and REVISIT the premise (is the bug really here? has the user re-run since the last fix?) — do NOT re-issue the same edit or rewire based on the same assumption.' });
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        resolve({
          ok: false,
          note: 'AskCode is STILL WORKING on "' + now.title + '" after ' + Math.round(timeoutMs / 1000) + 's — treat the edit as in progress: tell the user it is applying in the editor, do NOT re-issue it and do NOT claim it is done.'
        });
      }
    }, pollMs);
  });
}

// Every other write tool is one workflowCopilotEdits op; ctx.applyEdits
// commits it to the canvas immediately and reports back what happened.
function runEditOp(ctx, op) {
  var outcome = (typeof ctx.applyEdits === 'function' ? ctx.applyEdits([op]) : null) || {};
  var errors = outcome.errors || [];
  if (errors.length > 0) return { error: errors[0] };
  return { ok: true, did: (outcome.applied || [])[0] || 'applied' };
}

function normalizeWriteOp(name, args) {
  var a = args || {};
  switch (name) {
    case 'setWorkflowName': return { op: 'setWorkflowName', name: a.name };
    case 'addNode': return { op: 'addNode', ty: a.ty, node: a.node || {}, after: a.after || a.sourceNodeId, branch: a.branch || a.br, label: a.label };
    case 'updateNode': return { op: 'updateNode', id: a.id, set: a.set };
    case 'deleteNode': return { op: 'deleteNode', id: a.id };
    case 'addConnection':
      // Accept both { connection: {f,t,...} } and bare { f, t, ... } args.
      return { op: 'addConnection', connection: a.connection || a };
    case 'updateConnection': return { op: 'updateConnection', id: a.id, from: a.from || a.f, to: a.to || a.t, set: a.set };
    case 'deleteConnection': return { op: 'deleteConnection', id: a.id, from: a.from || a.f, to: a.to || a.t };
    default: return null;
  }
}

var READ_TOOLS = {
  grepWorkflow: toolGrepWorkflow,
  getNode: toolGetNode,
  listNodes: toolListNodes
};

// --- AI review (browser builds the minimal payload; server only runs the AI) --
//
// Slimming happens HERE, on the live canvas state, so:
//   - the review reflects UNSAVED local changes (what the user actually sees);
//   - the heavy code/prompts never leave the browser (no token leak into the
//     request or server logs) — the flow review sends names + wiring only.

function nodeType(node) {
  return String(node.templateId || '').indexOf('sys-') === 0
    ? String(node.templateId).slice(4)
    : (node.category === 'custom' ? 'custom' : (node.icon || 'node'));
}

// FLOW payload: node names + the ENTIRE wiring, plus the little config needed to
// spot empty conditions / mis-set Set Variables. No code, prompts, or hp.
function slimForFlowReview(state) {
  var s = state || {};
  var nodes = (s.nodes || []).map(function(node) {
    var out = { i: node.id, t: node.title || node.label || node.id, ty: nodeType(node) };
    if (node.conditionRule) out.cr = node.conditionRule;
    var pv = node.paramValues || {};
    if (pv.variable !== undefined) {
      out.pv = { variable: pv.variable, source: pv.source, mode: pv.mode, value: pv.value };
    } else if (pv.labels !== undefined) {
      // Classifier/Router labels — needed to judge the fa anchor wiring.
      out.pv = { labels: pv.labels };
    } else if (node.icon === 'variables') {
      // Variables default — a var with a default is validly read even when
      // no Set Variable writes it (constants like "Max Rounds").
      out.pv = { dataType: pv.dataType, value: pv.value };
    }
    return out;
  });
  var connections = (s.connections || []).map(function(c) {
    var out = { f: c.from, t: c.to };
    if (c.branch) out.br = c.branch;
    // Label anchors distinguish classifier/router edges; geometric anchors are noise.
    if (String(c.fromAnchor || '').indexOf('classifier-') === 0) out.fa = c.fromAnchor;
    if (c.propPath) out.pp = c.propPath;
    if (c.noData) out.nd = true;
    return out;
  });
  return { name: s.name, n: nodes, c: connections };
}

// NODE payload: one node's substance (code/prompt/params) + the connections
// that touch it, so the reviewer sees its real inputs/outputs. This is the only
// path that sends code — per node, on demand.
function slimForNodeReview(state, nodeId) {
  var s = state || {};
  var node = (s.nodes || []).find(function(n) { return n && n.id === nodeId; });
  if (!node) return null;
  var touching = (s.connections || []).filter(function(c) { return c.from === nodeId || c.to === nodeId; })
    .map(function(c) {
      var out = { f: c.from, t: c.to };
      if (c.branch) out.br = c.branch;
      if (c.propPath) out.pp = c.propPath;
      return out;
    });
  return {
    node: {
      i: node.id, t: node.title || node.label || node.id, ty: nodeType(node),
      gp: node.generationPrompt, co: node.code, pv: node.paramValues,
      cr: node.conditionRule, hp: node.hyperparameters, id: node.inputDescription
    },
    connections: touching
  };
}

function postReview(mode, payload) {
  return apiClient.request(REVIEW_ENDPOINT, { method: 'POST', body: { mode: mode, payload: payload } })
    .then(function(resp) { return (resp && resp.data) || { anomalies: [], ok: false, note: 'Empty review response.' }; })
    .catch(function(err) { return { error: (err && err.message) || 'Review request failed.' }; });
}

function runReviewWorkflow(ctx) {
  return postReview('flow', slimForFlowReview(ctx.getWorkflowState() || {}));
}

function runReviewNode(args, ctx) {
  var nodeId = String((args && (args.nodeId || args.id)) || '').trim();
  if (!nodeId) return Promise.resolve({ error: 'aiReviewNode: provide "nodeId".' });
  var payload = slimForNodeReview(ctx.getWorkflowState() || {}, nodeId);
  if (!payload) return Promise.resolve({ error: 'No node with id "' + nodeId + '".' });
  return postReview('node', payload);
}

// --- Public API --------------------------------------------------------------

// Returns a promise (matches doctorChatTools.runClientTool) resolving to the
// tool result object the server feeds back to the model.
function runClientTool(name, args, ctx) {
  return Promise.resolve().then(function() {
    // getNodeOutput reads the RAW state (lastOutput is stripped from the
    // compressed form the other read tools use).
    if (name === 'getNodeOutput') {
      return toolGetNodeOutput(args, ctx.getWorkflowState() || {});
    }
    if (READ_TOOLS[name]) {
      var wf = compressForCopilot(ctx.getWorkflowState());
      // getNode also reads the RAW state: inputStream needs source icons and
      // lastOutput shapes, both stripped from the compressed form — plus the
      // ephemeral live variable values (not part of workflowState at all).
      return READ_TOOLS[name](args, wf, ctx.getWorkflowState() || {}, getLiveVariableValues(ctx));
    }
    // AI reviews — browser slims the live canvas and posts only the minimal
    // payload (flow: names+wiring; node: one node's code+its edges).
    if (name === 'aiReviewWorkflow') return runReviewWorkflow(ctx);
    if (name === 'aiReviewNode') return runReviewNode(args, ctx);
    if (name === 'addNode') {
      var ty = String((args && args.ty) || 'custom');
      if (ty === 'custom' || ty === 'agent') return runGenerateCustomNode(args, ctx);
    }
    if (name === 'editNodeCode') {
      return runEditNodeCode(args, ctx);
    }
    // Screenshot of the entire canvas → { imageDataUrl } — the server turns
    // it into an image block so the model can SEE and analyze the graph.
    if (name === 'screenshotWorkflow') {
      if (typeof ctx.captureCanvas !== 'function') {
        return { error: 'Canvas capture is not available right now.' };
      }
      return Promise.resolve(ctx.captureCanvas()).then(function(shot) {
        if (!shot || !shot.dataUrl) {
          return { error: 'Could not capture the canvas (workflow may be empty or still rendering).' };
        }
        return {
          imageDataUrl: shot.dataUrl,
          note: 'Screenshot of the entire workflow canvas attached — analyze the image above.'
        };
      });
    }
    var op = normalizeWriteOp(name, args);
    if (op) return runEditOp(ctx, op);
    return { error: 'Unknown client tool: ' + name };
  }).catch(function(err) {
    return { error: (err && err.message) || String(err) };
  });
}

module.exports = {
  runClientTool: runClientTool,
  compressForCopilot: compressForCopilot
};
